import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import { setAuth, consumePostLoginRedirect, consumeSignedOutByTimeout } from '../../utils/authStorage';
import { apiFetch } from '../../utils/apiFetch';
import { setUserScopedString } from '../../utils/userScopedStorage';
import { readRecentWorkspaces } from '../ProjectListPage/pinFavStorage';
import { AuthPage, EyeIcon } from './AuthShell';
import { trackPageview, trackClick } from '../../utils/siteTracker';
import './LoginPage.css';

// Where to land after sign-in, honouring Account Settings → "Default
// landing page" (user.preferences.defaultLanding). The account page had
// promised this behaviour in a comment for some time; nothing actually
// read the preference until now. Must run AFTER setAuth (apiFetch needs
// the stored token). Falls back to home on any miss.
async function resolveDefaultLanding(userId) {
  try {
    const r = await apiFetch('/api/me/preferences');
    const d = await r.json();
    const pref = d?.preferences?.defaultLanding;
    if (pref === 'my-tasks') return '/my-tasks';
    if (pref === 'last') {
      const last = readRecentWorkspaces(userId)[0];
      if (last?.id) return `/project/${last.id}`;
    }
    if (typeof pref === 'string' && pref.startsWith('org:')) {
      // Land on that project's workspace list: set the active scope the
      // projects view reads, then open it.
      setUserScopedString('pidly.activeScope', pref.slice(4));
      return '/projects';
    }
  } catch { /* fall through to home */ }
  return '/overview';
}

function LoginPage() {
  // A join link brought them here (/login?join=<token>): open on sign-up,
  // and after the email is verified they land back on the link. An
  // invitation email's link (/login?signup=1) opens on sign-up too.
  const [joinToken] = useState(() => { try { return new URLSearchParams(window.location.search).get('join') || ''; } catch { return ''; } });
  const [openOnSignup] = useState(() => { try { return new URLSearchParams(window.location.search).get('signup') === '1'; } catch { return false; } });
  const [mode, setMode] = useState(joinToken || openOnSignup ? 'signup' : 'signin'); // 'signin' | 'signup' | 'verify' | 'twofactor'
  const [ticket, setTicket] = useState('');   // from sign-in, when two-factor is on
  const [otp, setOtp] = useState('');
  const [resent, setResent] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Default ON so the session lands in localStorage (shared across tabs +
  // survives a browser restart) — opening Pidly in a new tab then stays
  // signed in instead of bouncing back to this login page. Users who want a
  // per-tab session can still untick it.
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // The inactivity timeout signs the user out silently and lands them
  // here — without this notice the bounce reads as the app forgetting
  // them. Consumed once (lazy initializer) so it doesn't linger.
  const [timedOut] = useState(consumeSignedOutByTimeout);
  const navigate = useNavigate();

  // Visit statistics: the sign-up form and the check-your-email screen are
  // modes of /login, so they are counted as their own pages.
  useEffect(() => {
    if (mode === 'signup') trackPageview('/login/signup');
    else if (mode === 'verify') trackPageview('/login/check-email');
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (mode === 'signup' && (!acceptTerms || !acceptPrivacy)) {
      setError('Please accept the Terms and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      const endpoint = mode === 'signup' ? '/api/auth/signup' : '/api/auth/signin';
      const body = mode === 'signup'
        ? { email, password, firstName: firstName.trim(), lastName: lastName.trim() }
        : { email, password };

      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Something went wrong');
        setLoading(false);
        return;
      }

      // If signup requires email verification
      if (data.requiresVerification) {
        setMode('verify');
        setLoading(false);
        return;
      }
      // Two-factor: the password was right; now the code from the app.
      if (data.requires2fa) {
        setTicket(data.ticket); setOtp(''); setError('');
        setMode('twofactor');
        setLoading(false);
        return;
      }

      // Persist auth: rememberMe → localStorage (persists), otherwise sessionStorage (cleared on browser close)
      setAuth({ token: data.token, user: data.user, remember: mode === 'signup' ? true : rememberMe });

      // Honour a deep-link that bounced through login (invite links etc.);
      // with no deep link, the account's default-landing preference decides.
      const target = consumePostLoginRedirect();
      navigate(target && target !== '/' ? target : await resolveDefaultLanding(data.user?.id));
    } catch (err) {
      setError('Cannot connect to server');
      setLoading(false);
    }
  };

  // The second factor: a six-digit code from the app, or a recovery code.
  const submitOtp = async (e) => {
    e?.preventDefault?.();
    if (!otp.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ticket, code: otp.trim() }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'That code is not right'); setLoading(false); return; }
      setAuth({ token: data.token, user: data.user, remember: rememberMe });
      const target = consumePostLoginRedirect();
      navigate(target && target !== '/' ? target : await resolveDefaultLanding(data.user?.id));
    } catch {
      setError('Cannot connect to server');
      setLoading(false);
    }
  };
  if (mode === 'twofactor') {
    return (
      <AuthPage>
        <form className="login-form" onSubmit={submitOtp}>
          <h2 className="login-2fa-title">Enter your code</h2>
          <p className="login-note">Open your authenticator app and type the six-digit code for pidly. Lost the app? A recovery code works once.</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            aria-label="Code"
          />
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-submit-btn" disabled={loading || !otp.trim()}>{loading ? 'Checking…' : 'Sign in'}</button>
          <p className="login-resend"><button type="button" className="login-link-btn" onClick={() => { setMode('signin'); setTicket(''); setOtp(''); setError(''); }}>Back</button></p>
        </form>
      </AuthPage>
    );
  }

  // Email verification sent screen
  if (mode === 'verify') {
    return (
      <AuthPage>
        <div className="login-verify">
          <svg className="login-verify-icon login-verify-icon--success" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
          </svg>
          <h2>Check your email</h2>
          <p>We've sent a verification link to <strong>{email}</strong></p>
          <p>Click the link to verify your address{joinToken ? ' and you will come back to the project link' : ', then sign in'}.</p>
          <p>pidly approves each new account before it opens. You will get an email when yours is ready.</p>
          <p className="login-resend">
            {resent || <>Nothing arrived? <button type="button" className="login-link-btn" onClick={async () => {
              try {
                await fetch(`${BACKEND_URL}/api/auth/resend-verification`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
                setResent('Sent again. Check your spam folder too.');
              } catch { setResent('Could not send it again. Try in a minute.'); }
            }}>Send it again</button></>}
          </p>
          <button className="login-submit-btn" onClick={() => { setMode('signin'); setError(''); }}>
            Back to Sign In
          </button>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <h1 className="login-subtitle">{mode === 'signup' ? 'Create your account' : 'Sign in'}</h1>
      {/* Signing in needs no line under it: the heading says what the page
          is for, and "Pick up where you left off" was a sentence between a
          person and the field they came to type in. Creating an account
          still does, because it has a condition to state. */}
      {mode !== 'signin' && (
        <p className="login-lede">
          {joinToken ? 'Create your account to join the project.' : 'pidly approves each new account before it opens.'}
        </p>
      )}

      {timedOut && mode === 'signin' && (
        <div className="login-info">You were signed out after a period of inactivity.</div>
      )}

      <form className="login-form" onSubmit={handleSubmit}>
        {mode === 'signup' && (
          <div className="login-name-row">
            <label className="login-field">
              <span className="login-field-label">First name</span>
              <input
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
                required
              />
            </label>
            <label className="login-field">
              <span className="login-field-label">Last name</span>
              <input
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </label>
          </div>
        )}
        {mode === 'signup' && joinToken && (
          <p className="login-note">Create your account and verify your email, and you will come back to the project link.</p>
        )}

        {/* Sign-in accepts EMAIL OR PHONE in the one field (the backend
            routes on the presence of '@' and matches a phone against the
            account's profile phone). Signup stays email-only — email is
            the primary identity. */}
        <label className="login-field">
          <span className="login-field-label">{mode === 'signin' ? 'Email or phone' : 'Email'}</span>
          <input
            type={mode === 'signin' ? 'text' : 'email'}
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus={mode === 'signin'}
            required
          />
        </label>

        <div className="login-field">
          <label className="login-field-label" htmlFor="login-password">Password</label>
          <div className="login-password-wrapper">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
            <button type="button" className="login-eye-btn" onClick={() => setShowPassword(v => !v)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              <EyeIcon open={showPassword} />
            </button>
          </div>
        </div>

        {mode === 'signup' && (
          <div className="login-field">
            <label className="login-field-label" htmlFor="login-password-confirm">Confirm password</label>
            <div className="login-password-wrapper">
              <input
                id="login-password-confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={6}
                required
              />
              <button type="button" className="login-eye-btn" onClick={() => setShowConfirmPassword(v => !v)} tabIndex={-1} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                <EyeIcon open={showConfirmPassword} />
              </button>
            </div>
          </div>
        )}

        {mode === 'signup' && (
          <div className="login-checkboxes">
            {/* The Terms / Privacy names are plain text, not links — the
                documents don't exist yet. When they do, link them here. */}
            <label className="login-checkbox">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required />
              <span>I agree to the <span className="login-legal-term">Terms and Conditions</span></span>
            </label>
            <label className="login-checkbox">
              <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} required />
              <span>I agree to the <span className="login-legal-term">Privacy Policy</span></span>
            </label>
            <label className="login-checkbox">
              <input type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} />
              <span>Send me product updates and news <span className="login-checkbox-optional">(optional)</span></span>
            </label>
          </div>
        )}

        {mode === 'signin' && (
          <div className="login-checkboxes">
            <label className="login-checkbox">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              <span>Remember me on this device</span>
            </label>
          </div>
        )}

        {error && <div className="login-error">{error}</div>}

        {/* A button that SAYS what it does: the round chevron read as a
            "next" arrow, and on sign-up nothing on the page said "Create
            account" until the switch link below, which goes the other way. */}
        <button type="submit" className="login-submit-btn login-submit-btn--main" disabled={loading} onClick={() => trackClick(mode === 'signup' ? 'Create Account (submit)' : 'Sign In (submit)')}>
          {loading ? (mode === 'signup' ? 'Creating…' : 'Signing in…') : (mode === 'signup' ? 'Create account' : 'Sign in')}
        </button>
      </form>

      <div className="login-switch">
        {mode === 'signin' ? (
          <>
            <button className="login-link" onClick={() => { trackClick('Create Account'); setMode('signup'); setError(''); }}>Create Account</button>
            <span className="login-switch-sep">/</span>
            <button className="login-link" onClick={() => { trackClick('Forgot Password'); navigate('/reset-password'); }}>Forgot Password</button>
          </>
        ) : (
          <button className="login-link" onClick={() => { setMode('signin'); setError(''); }}>Already have an account? Sign in</button>
        )}
      </div>
    </AuthPage>
  );
}

export default LoginPage;
